import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
} from "@material-ui/core";
import TemplateCard from "./../TemplateCard/TemplateCard";
import PublishDialog from "../../../TemplateBuilder/components/PublishDialog/PublishDialog";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { publishFormV2, renameTemplate, softDeleteTemplate } from "../../../../services/templateV2Service";
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  BorderColor as BorderColorIcon,
} from "@material-ui/icons";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import moment from "moment";
import TablePagination from "@mui/material/TablePagination";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import { BaseDataTable } from "../../../../components/common/BaseDataTable";
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';

const useStyles = makeStyles((theme) => ({
  container: {
    width: "100%",
    height: "calc(100vh - 260px)",
    "@supports (height: 100dvh)": {
      height: "calc(100dvh - 260px)",
    },
    borderRadius: "8px !important",
    backgroundColor: "#fff",
    display: "flex",
    flexDirection: "column",
    padding: "10px",
    position: "relative",
    overflowY: "auto",
  },
  paginationSticky: {
    position: "sticky",
    bottom: 0,
    backgroundColor: "#fff",
    borderTop: "1px solid #E5E7EB",
    paddingTop: 6,
    marginTop: 6,
    zIndex: 2,
  },
  gridContainer: {
    display: "grid",
    gridGap: "14px",
    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
    gridAutoRows: "120px",
    padding: "8px 0",
  },
  listContainer: {
    display: "flex",
    flexDirection: "column",
    padding: "0",
    border: "1px solid #eee",
  },
  scrollListBody: {
    maxHeight: "calc(100vh - 250px)",
    overflowY: "auto",
  },
  listHeader: {
    display: "grid",
    gridTemplateColumns: "3fr 1fr 1fr 1.5fr 0.8fr",
    alignItems: "center",
    backgroundColor: "#F3F4F6",
    color: "#111827",
    fontWeight: "bold",
    fontSize: "13px",
    height: "50px",
    padding: "0 8px",
    borderTopLeftRadius: "6px",
    borderTopRightRadius: "6px",
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  headerCell: { display: "flex", alignItems: "center", cursor: "pointer" },
  listItem: {
    display: "grid",
    gridTemplateColumns: "3fr 1fr 1fr 1.5fr 0.8fr",
    alignItems: "center",
    padding: "0 8px",
    fontSize: "13px",
    height: "52px",
    boxSizing: "border-box",
    textAlign: "left",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": { backgroundColor: "#f0f8ff" },
  },
  rowEven: { backgroundColor: "#ffffff" },
  rowOdd: { backgroundColor: "#f9f9f9" },
  templateNameContainer: { display: "flex", alignItems: "center", gap: "8px" },
  templateName: {
    color: "#000000",
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "pointer",
  },
  templateNameButton: {
    appearance: "none",
    border: "none",
    background: "transparent",
    padding: 0,
    margin: 0,
    textAlign: "left",
    font: "inherit",
  },
  blueText: { color: "#0076FF" },
  skeleton: {
    backgroundColor: "#f0f0f0",
    borderRadius: "4px",
    animation: "$skeleton-loading 1.5s infinite ease-in-out",
  },
  "@keyframes skeleton-loading": {
    "0%": { backgroundColor: "#f0f0f0" },
    "50%": { backgroundColor: "#e0e0e0" },
    "100%": { backgroundColor: "#f0f0f0" },
  },
  skeletonText: {
    backgroundColor: "#f0f0f0",
    borderRadius: "4px",
    animation: "$skeleton-loading 1.5s infinite ease-in-out",
    height: "1em",
    marginBottom: "0.5em",
    "&:last-child": { marginBottom: 0 },
  },
}));

export default function TemplateGrid({
  setLoading,
  reloadTemplates,
  onArchiveChanged,
  viewMode = "grid",
  templates = [],
  loading = false,
  pageDetails = {},
  currentPage = 0,
  pageSize = 50,
  totalRecords = 0,
  onPageChange,
  onSort,
  sortBy,
  sortOrder,
}) {
  const classes = useStyles();
  const [openPublish, setOpenPublish] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const navigate = useNavigate();

  const formatDate = (dateString) =>
    dateString ? moment(dateString).format("MMM D, YYYY") : "N/A";

  const columns = [
    {
      field: "templateName",
      headerName: "Name",
      flex: 3,
      headerClassName: "MuiTableHead-root",
      renderCell: (params) => (
        <div className={classes.templateNameContainer}>
          {isPdfFile(params.row) && (
            <PictureAsPdfIcon style={{ color: "#E53935", marginRight: 6 }} />
          )}
          <button
            type="button"
            onClick={() => handleClickMenuItem("EditTemplate", params.row)}
            className={`${classes.templateName} ${classes.templateNameButton}`}
            title={params.row.templateName}
          >
            {params.row.templateName}
          </button>
        </div>
      ),
    },
    {
      field: "creatorRole",
      headerName: "Team",
      headerClassName: "MuiTableHead-root",
      flex: 1,
      renderCell: (params) => (
        <span style={{ opacity: 0.7 }}>
          {params.value || params.row?.creatorRole || "Unassigned"}
        </span>
      ),
    },
    {
      field: "fileSize",
      headerName: "Size",
      headerClassName: "MuiTableHead-root",
      flex: 1,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => <span style={{ opacity: 0.7 }}>{params.value || "—"}</span>,
    },
    {
      field: "createdAt",
      headerName: "Date Added",
      headerClassName: "MuiTableHead-root",
      flex: 1.5,
      valueFormatter: (params) => formatDate(params.value),
      align: "right",
      headerAlign: "right",
      renderCell: (params) => <span style={{ opacity: 0.7 }}>{formatDate(params.value)}</span>,
    },
    {
      field: "actions",
      headerName: "",
      headerClassName: "MuiTableHead-root",
      flex: 0.1,
      renderCell: (params) => (
        // <IconButton
        //   size="small"
        //   onClick={(e) => handleMenuClick(e, params.row)}
        // >
        <MoreVertIcon
          onClick={(e) => handleMenuClick(e, params.row)}
          style={{ cursor: "pointer" }}
        />
        // </IconButton>
      ),
    },
  ];

  const isPdfFile = (t) => {
    const candidate =
      t?.pdfUrl || t?.pathUrl || t?.fileName || t?.templateName || "";
    return String(candidate).toLowerCase().includes(".pdf");
  };

  const [recipients, setRecipients] = useState([
    { name: "", email: "", order: 1 },
  ]);

  // Pagination is handled by DataGrid in list view (server mode)

  const handleMenuClick = (e, template) => {
    setMenuAnchorEl(e.currentTarget);
    setSelectedTemplate(template);
  };
  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleClosePublish = () => setOpenPublish(false);

  const handleClickMenuItem = (menu, template) => {
    setSelectedTemplate(template);
    switch (menu) {
      case "Send":
        setOpenPublish(true);
        break;
      case "EditTemplate":
        navigate(`/templates-v2/template-builder/${template.id}?mode=edit`);
        break;
      case "Rename": {
        const tid =
          template?.id ||
          template?.formTemplateId ||
          template?.FormTemplateId;
        const currentName = template?.templateName || "";
        handleMenuClose();
        void (async () => {
          const { value: newName, isConfirmed } = await Swal.fire({
            title: "Rename template",
            input: "text",
            inputValue: currentName,
            showCancelButton: true,
            confirmButtonText: "Save",
            inputValidator: (value) => {
              const v = value != null ? String(value).trim() : "";
              if (!v) return "Please enter a name";
              if (v.length > 200) return "Name must be 200 characters or less";
              return undefined;
            },
          });
          if (!isConfirmed || newName == null) return;
          const trimmed = String(newName).trim();
          if (!trimmed || trimmed === currentName.trim()) return;
          try {
            setLoading(true, "Renaming template...");
            await renameTemplate(tid, trimmed);
            toast.success("Template renamed.");
            reloadTemplates && reloadTemplates();
          } catch (err) {
            console.error("rename template", err);
            toast.error("Could not rename template. Please try again.");
          } finally {
            setLoading(false);
          }
        })();
        return;
      }
      case "Delete":
        handleDeleteTemplate(template.id);
        break;
      default:
        break;
    }
    handleMenuClose();
  };

  const handleDeleteTemplate = async (templateId) => {
    try {
      const result = await Swal.fire({
        title: "Archive this template?",
        text: "You can restore it later from Archive.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, archive it!",
      });
      if (result.isConfirmed) {
        setLoading(true, "Archiving template...");
        softDeleteTemplate(templateId)
          .then(() => {
            Swal.fire("Archived!", "The template has been moved to Archive.", "success");
            reloadTemplates && reloadTemplates();
            onArchiveChanged && onArchiveChanged();
          })
          .catch(() =>
            toast.error("An error has occurred. Please try again later.")
          )
          .finally(() => setLoading(false));
      }
    } catch {
      toast.error("Error deleting template");
    }
  };


  const updateRecipients = async (templateId) => {
    // If templateId is provided, redirect to the new recipient assignment page
    if (templateId) {
      setOpenPublish(false);
      navigate(`/templates-v2/template-builder-recipients/${templateId}`);
      return;
    }

    // Fallback to original behavior if no templateId
    const docName =
      selectedTemplate?.templateName || selectedTemplate?.fileName || "";
    const docId =
      selectedTemplate?.id ||
      selectedTemplate?.formTemplateId ||
      selectedTemplate?.FormTemplateId ||
      null;

    setOpenPublish(false);
    const signers = recipients.map((r) => ({
      name: r.name,
      email: r.email,
      order: String(r.order),
    }));
    const body = {
      documentName: docName,
      formTemplateId: docId,
      userRequest: signers,
    };
    console.log(
      "publish body",
      body,
      "selectedTemplate",
      selectedTemplate,
      "recipients",
      recipients
    );
    setLoading(true);
    try {
      await publishFormV2(body, () => { });
      toast.success("The form has been published successfully.");
    } catch (err) {
      console.error("publish error", err);
      toast.error("An error has occurred. Please try again later.");
    } finally {
      setLoading(false);
      setOpenPublish(false);
      setSelectedTemplate(null);
    }
  };

  return (
    <div className={classes.container}>
      {loading ? (
        <SkeletonLoader viewMode={viewMode} itemCount={pageSize} />
      ) : (
        <>
          {viewMode === "grid" ? (
            <div className={classes.gridContainer}>
              {templates.map((template, idx) => (
                <TemplateCard
                  key={`${template.id}-${idx}`}
                  template={template}
                  onClickMenu={handleClickMenuItem}
                />
              ))}
            </div>
          ) : (
            <>
              <BaseDataTable
                data={templates.map((template) => ({
                  ...template,
                  id: template.id || template.formTemplateId,
                  fileSize: template.fileSizeFormatted || "N/A",
                }))}
                columns={columns}
                loading={loading}
                onMenuOptionClick={handleClickMenuItem}
                option={({ action, id, row }) => {
                  handleClickMenuItem(action, row);
                }}
                hideFooter
                autoHeight
                wrapperAutoHeight
              />
              <div style={{ padding: "6px 8px", color: "#6B7280", fontSize: 12 }}>
                Showing {templates.length} of {totalRecords} templates
              </div>
              {!loading && totalRecords > 0 && (
                <div className={classes.paginationSticky}>
                  <TablePagination
                    component="div"
                    count={totalRecords}
                    page={currentPage}
                    onPageChange={(_e, nextPage) => onPageChange?.(nextPage, pageSize)}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={(e) =>
                      onPageChange?.(0, Number.parseInt(e.target.value, 10))
                    }
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    showFirstButton
                    showLastButton
                  />
                </div>
              )}
            </>
          )}
        </>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={() => handleClickMenuItem("Send", selectedTemplate)}>
          <ListItemIcon>
            <CloudUploadIcon fontSize="small" style={{ color: "#0076FF" }} />
          </ListItemIcon>
          <ListItemText primary="Send" />
        </MenuItem>
        <MenuItem onClick={() => handleClickMenuItem("Rename", selectedTemplate)}>
          <ListItemIcon>
            <BorderColorIcon fontSize="small" style={{ color: "#0076FF" }} />
          </ListItemIcon>
          <ListItemText primary="Rename" />
        </MenuItem>
        <MenuItem onClick={() => handleClickMenuItem("EditTemplate", selectedTemplate)}>
          <ListItemIcon>
            <EditIcon fontSize="small" style={{ color: "#0076FF" }} />
          </ListItemIcon>
          <ListItemText primary="Edit" />
        </MenuItem>
        <MenuItem
          onClick={() => handleClickMenuItem("Delete", selectedTemplate)}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" style={{ color: "#A8071A" }} />
          </ListItemIcon>
          <ListItemText
            primary="Archive"
            primaryTypographyProps={{ style: { color: "#A8071A" } }}
          />
        </MenuItem>
      </Menu>

      <PublishDialog
        open={openPublish}
        onClose={handleClosePublish}
        recipients={recipients}
        setRecipients={setRecipients}
        onPublish={updateRecipients}
        templateId={
          selectedTemplate?.id ||
          selectedTemplate?.formTemplateId ||
          selectedTemplate?.FormTemplateId
        }
      />
    </div>
  );
}
